import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionServerSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { MapDispatchToProps } from '~src/commonInfra/utils/ConnectUtils';
import { ConnectedApplicationSubspaceRouter } from '~src/connectedApplications/components/ConnectedApplicationSubspaceRouter';
import { HomeUserUtils } from '~src/home/utils/HomeUserUtils';
import { SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import {
  IntegrationsSiteSettingsSubspaceComponent,
  IntegrationsSubspaceName
} from '~src/settings/IntegrationsSettings/IntegrationsSiteSettingsSubspace';
import { MobileSettingsSubspaceName } from '~src/settings/mobile-settings/MobileSecuritySettingsShared';
import { fetchPrivateConnectionsRequest } from '~src/settings/privateconnect-settings/actions/FetchPrivateConnectionsRequestAction';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { AuthenticationSubspaceRouter } from '~src/settings/siteAuthSettings/components/AuthenticationSubspaceRouter';
import { Sites } from '~src/siteManagement/services/resources/Sites';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { SiteSettingsSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import { SiteRoles } from '~src/userManagement/users/serverApi/types/ISiteRole';

import { AppState } from '../../../../bootstrap/redux/state/AppState';
import { PrivateConnectSiteSettingsSubspace } from '../../../privateconnect-settings/PrivateConnectSiteSettingsSubspace';
import { AuthenticationSiteSettingsSubspace } from './AuthenticationSettingsSubspace';
import { BridgeSiteSettingsSubspace } from './BridgeSiteSettingsSubspace';
import { DataConnectSiteSettingsSubspace } from './DataConnectSiteSettingsSubspace';
import { ExtensionsSiteSettingsSubspaceComponent } from './extensions/ExtensionsSiteSettingsSubspace';
import { GeneralSiteSettingsSubspace } from './GeneralSiteSettingsSubspace';
import { MobileSiteSettingsSubspaceComponent } from './MobileSiteSettingsSpace';

export interface SiteSettingsSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly siteName: string;
  readonly subspaceName: SiteSettingsSubspaceName;
}

export interface SiteSettingsServicesProps {
  readonly server: SessionManagementService;
}

export interface SiteSettingsStateProps {
  readonly doesTheServerHaveMoreThanOneSite: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly userRole: string;
  readonly isOnlineEnvironment: boolean;
  readonly stateSubspaceName: SiteSettingsSubspaceName;
  readonly privateConnectEnabled: boolean;
}

export interface SiteSettingsServicesDispatchProps {
  fetchPrivateConnections: (server: SessionManagementService) => void;
}

export type SiteSettingsNonServicesProps = SiteSettingsSpaceOwnProps & SiteSettingsStateProps;
export type SiteSettingsSpaceProps = SiteSettingsNonServicesProps & SiteSettingsServicesProps & SiteSettingsServicesDispatchProps;

export class SiteSettingsSpaceComponent extends React.PureComponent<SiteSettingsSpaceProps> {

  public componentDidMount(): void {
    if(this.props.isOnlineEnvironment){
      this.props.fetchPrivateConnections(this.props.server);
    }
  }

  public render() {
    const subspaces = getSettingsSubspaces(
      this.props.urls,
      this.props.siteName,
      this.props.server,
      this.props.isSessionServerAdmin,
      this.props.userRole,
      this.props.isOnlineEnvironment,
      this.props.subspaceName,
      this.props.stateSubspaceName,
      this.props.privateConnectEnabled
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subspaceName}
        urlForSubspace={this.getSettingsUrl}
        mastheadElement={null}
        testIdPrefix={'site-settings'}
        spaceItemName={this.props.doesTheServerHaveMoreThanOneSite ? Messages.siteSettings() : Messages.settings()}
      />
    );
  }

  private static settingsUrlBySubspace: Record<SiteSettingsSubspaceName, (urls: SpacesSiteUrls) => string> = {
    settings: (urls) => urls.settingsUrl(),
    extensions: (urls) => urls.settingsExtensionsUrl(),
    bridge: (urls) => urls.settingsBridgeUrl(),
    dataconnect: (urls) => urls.settingsDataConnectUrl(),
    authentication: (urls) => urls.settingsAuthenticationUrl(),
    integrations: (urls) => urls.settingsIntegrationsUrl(),
    connectedApplications: (urls) => urls.settingsConnectedApplicationsUrl(),
    mobile: (urls) => urls.settingsMobileUrl(),
    privateconnect: (urls) => urls.settingsPrivateConnectUrl(),
  };

  private getSettingsUrl = (subspaceName?: SiteSettingsSubspaceName) => {
    if (subspaceName) {
      return SiteSettingsSpaceComponent.settingsUrlBySubspace[subspaceName](this.props.urls);
    }
    return '';
  };
}

export function getSettingsSubspaces(
  urls: SpacesSiteUrls,
  siteName: string,
  server: SessionManagementService,
  isSessionServerAdmin: boolean,
  userRole: string,
  isOnlineEnvironment: boolean,
  subspaceName: SiteSettingsSubspaceName,
  stateSubspaceName: SiteSettingsSubspaceName,
  privateConnectEnabled:  boolean
): ReadonlyArray<Subspace> {
  return _.compact<_.List<Subspace | false>>([
    {
      name: 'settings',
      tabLabel: Messages.general(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <GeneralSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
          serverSettingsService={ServerSettingsInstance}
        />
      )
    },
    ServerSettingsUnauthenticated.isSiteSamlEnabled() && {
      name: 'authentication',
      tabLabel: Messages.authentication(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        FeatureFlags.featureFlagForMultipleIDPSupportEnabled ?
          <AuthenticationSubspaceRouter
            siteName={siteName}
            urls={urls}
            server={server}
            isSessionServerAdmin={isSessionServerAdmin}
            serverSettingsService={ServerSettingsInstance}
            sites={Sites.instance}/> :
          <AuthenticationSiteSettingsSubspace
            siteName={siteName}
            urls={urls}
            server={server}
            isSessionServerAdmin={isSessionServerAdmin}
            serverSettingsService={ServerSettingsInstance}
            sites={Sites.instance}
          />
      )
    },
    ServerSettingsUnauthenticated.isBridgeEnabled() && {
      name: 'bridge',
      tabLabel: Messages.bridge(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <BridgeSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    ServerSettingsUnauthenticated.isBridgeEnabled() && {
      name: 'dataconnect',
      tabLabel: Messages.dataConnect(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <DataConnectSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    FeatureFlags.extensionsSettingsTabEnabled && {
      name: 'extensions',
      tabLabel: Messages.extensionsSettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <ExtensionsSiteSettingsSubspaceComponent
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
        />
      )
    },
    isOnlineEnvironment && privateConnectEnabled && {
      name: 'privateconnect',
      tabLabel: Messages.privateconnect(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <PrivateConnectSiteSettingsSubspace
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
          isOnlineEnvironment={isOnlineEnvironment}
        />
      )
    },
    isUserAuthorizedForIntegrationsTab(userRole) && {
      name: IntegrationsSubspaceName,
      tabLabel: Messages.integrations_settingsTabTitle(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <IntegrationsSiteSettingsSubspaceComponent
          siteName={siteName}
          urls={urls}
          server={server}
          isSessionServerAdmin={isSessionServerAdmin}
          isOnlineEnvironment={isOnlineEnvironment}
          userRole={userRole}
          sites={Sites.instance}
        />
      )
    },
    {
      name: 'connectedApplications',
      tabLabel: Messages.connectedApplications(),
      //TODO: Add contentCount -- contentCount: 1,
      shouldRenderTabContentCount: false,
      renderContent: () => {
        if (subspaceName === stateSubspaceName) {
          return <ConnectedApplicationSubspaceRouter />;
        }
      }
    },
    {
      name: MobileSettingsSubspaceName,
      tabLabel: Messages.mobileSettings_title(),
      shouldRenderTabContentCount: false,
      renderContent: () => (
        <MobileSiteSettingsSubspaceComponent
          server={server}
        />
      )
    },
  ]);
}

export function isUserAuthorizedForIntegrationsTab(userRole: String) {
  const isAdminCreator = (userRole === SiteRoles.ServerAdministrator) || (userRole === SiteRoles.SiteAdministratorCreator);
  const isAdminExplorer = userRole === SiteRoles.SiteAdministratorExplorer;

  return isAdminCreator || isAdminExplorer;
}

const mapDispatchToProps: MapDispatchToProps<SiteSettingsServicesDispatchProps> =
  (dispatch) => bindActionCreators({
    fetchPrivateConnections: fetchPrivateConnectionsRequest
  },
  dispatch);

export const ConnectedSiteSettingsSpace: React.ComponentClass<SiteSettingsSpaceOwnProps & SiteSettingsServicesProps>
  = connect((state: AppState) => ({
    isSessionServerAdmin: SessionUserSelectors.isSessionServerAdmin(state),
    userRole: HomeUserUtils.getRole(state),
    isOnlineEnvironment: SessionServerSelectors.isOnlineEnvironment(state),
    stateSubspaceName: selectSpaceInfo(state).subspaceName,
    privateConnectEnabled: state.privateConnectInfo.privateConnections.length > 0
  }), mapDispatchToProps)(SiteSettingsSpaceComponent);

